import Http from '../lib/http'

export const ASSISTANT_WAIT_MS = 90000

function newKey() {
  const raw = `assist-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
  return raw.slice(0, 80)
}

function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export function assistantLocale(locale) {
  return String(locale || '').toLowerCase().startsWith('es') ? 'es' : 'en'
}

export function nextPollDelay(elapsedMs) {
  if (elapsedMs < 2000) return 400
  return 2000
}

export async function pollAssistantMessage(messageId, options = {}) {
  const budgetMs = options.budgetMs ?? ASSISTANT_WAIT_MS
  const now = options.now || Date.now
  const wait = options.wait || delay
  const shouldStop = options.shouldStop || (() => false)
  const started = now()

  while (!shouldStop()) {
    const row = await Http.get(`api/assistant/messages/${messageId}`)
    const data = row?.data?.data
    if (data && (data.status === 'ready' || data.status === 'failed')) {
      return { message: data }
    }
    const elapsed = now() - started
    const pause = nextPollDelay(elapsed)
    if (elapsed + pause > budgetMs) {
      return { error: 'timeout', message: data || null }
    }
    await wait(pause)
  }

  return { stopped: true }
}

export async function loadConversationList() {
  const res = await Http.get('api/assistant/conversations?page=1&per_page=8')
  const rows = res?.data?.data
  return Array.isArray(rows) ? rows : []
}

export async function loadLatestConversation() {
  const res = await Http.get('api/assistant/conversations?page=1&per_page=1')
  const rows = res?.data?.data
  if (!Array.isArray(rows) || !rows[0] || !rows[0].id) return null
  return rows[0]
}

function feedbackFailed(res) {
  return !res || typeof res.status !== 'number' || res.status >= 400 || res.data?.status === false
}

export async function saveMessageFeedback(messageId, rating, reason) {
  const res = await Http.put(`api/assistant/messages/${messageId}/feedback`, {
    rating,
    reason: reason || null,
  })
  if (feedbackFailed(res)) {
    const error = new Error('feedback_failed')
    error.code = res?.data?.code || 'feedback_failed'
    throw error
  }
  return res.data?.data || null
}

export async function clearMessageFeedback(messageId) {
  const res = await Http.delete(`api/assistant/messages/${messageId}/feedback`)
  if (feedbackFailed(res)) {
    const error = new Error('feedback_failed')
    error.code = res?.data?.code || 'feedback_failed'
    throw error
  }
  return null
}

export async function uploadAssistantImage(file) {
  const body = new FormData()
  body.append('image', file)
  const res = await Http.post('api/assistant/attachments', body, {
    transformRequest: [(data, headers) => {
      delete headers['Content-Type']
      delete headers['content-type']
      return data
    }],
  })
  if (feedbackFailed(res)) {
    const error = new Error('image_failed')
    error.code = res?.data?.code || 'invalid_image'
    throw error
  }
  return res.data?.data || null
}

export async function fetchAssistantAttachment(id) {
  const res = await Http.get(`api/assistant/attachments/${id}`, { responseType: 'blob' })
  if (feedbackFailed(res) || !res.data) {
    throw new Error('attachment')
  }
  return res.data
}

export async function deleteAssistantAttachment(id) {
  const res = await Http.delete(`api/assistant/attachments/${id}`)
  if (feedbackFailed(res)) {
    const error = new Error('image_failed')
    error.code = res?.data?.code || 'image_failed'
    throw error
  }
  return null
}

export async function loadConversation(conversationId) {
  const res = await Http.get(`api/assistant/conversations/${conversationId}?per_page=20`)
  if (!res || res.status === 404) return null
  const messages = res?.data?.messages
  return Array.isArray(messages) ? messages : []
}

/**
 * Opens a thread when needed, posts one message, and polls until the job finishes.
 * Passing messageId resumes that attempt and does not post again.
 * A 503 or a missing service falls back to the local catalog.
 */
export async function askAssistant({
  content,
  locale,
  conversationId,
  attachmentIds,
  idempotencyKey,
  messageId,
  shouldStop,
  wait,
  now,
}) {
  let id = conversationId
  const key = idempotencyKey || newKey()
  if (!messageId && !id) {
    const opened = await Http.post('api/assistant/conversations', { locale })
    if (!opened || opened.status === 503 || opened.status >= 500) {
      return { fallback: true, idempotencyKey: key }
    }
    id = opened?.data?.data?.id
    if (!id) return { fallback: true, idempotencyKey: key }
  }

  let assistantId = messageId
  if (!assistantId) {
    const posted = await Http.post(
      `api/assistant/conversations/${id}/messages`,
      { content, locale, attachment_ids: attachmentIds || [] },
      { headers: { 'Idempotency-Key': key } },
    )
    if (!posted || posted.status === 503) return { fallback: true, conversationId: id, idempotencyKey: key }
    if (posted.status === 404) return { missing: true, conversationId: id, idempotencyKey: key }
    if (posted.status === 429) return { error: 'usage_limit', conversationId: id, idempotencyKey: key }
    assistantId = posted?.data?.data?.id
    if (!assistantId) return { fallback: true, conversationId: id, idempotencyKey: key }
  }

  const polled = await pollAssistantMessage(assistantId, { shouldStop, wait, now })
  return { ...polled, conversationId: id, idempotencyKey: key, messageId: assistantId }
}

export async function loadAssistantEnabled() {
  const res = await Http.get('api/assistant/settings')
  return res?.status === 200 && res?.data?.data?.enabled === true
}
